/**
 * useOrderedAssets - Computes the ordered list of assets for a channel,
 * including pending copies and uploads inserted at correct positions.
 */

import { useCallback } from 'react';
import type { ProductAsset } from '@/types/products';
import type { ChannelOrderState, PendingCopy, PendingUpload } from './types';

type GroupedByChannel = Record<string, ProductAsset[]>;

export function useOrderedAssets(
  groupedByChannel: GroupedByChannel,
  channelOrders: Map<string, ChannelOrderState>,
  pendingCopies: PendingCopy[],
  pendingUploads: PendingUpload[],
  changedChannelKeys?: Set<string>,
) {
  return useCallback(
    (channelKey: string): ProductAsset[] => {
      const channelAssets = groupedByChannel[channelKey] || [];
      const orderState = channelOrders.get(channelKey);

      let result: ProductAsset[];
      const orderChanged = !!orderState && JSON.stringify(orderState.originalOrder) !== JSON.stringify(orderState.currentOrder);
      const hasReorder = orderChanged || (!!orderState && !!changedChannelKeys?.has(channelKey));
      if (!orderState) {
        result = [...channelAssets];
      } else {
        const assetMap = new Map(channelAssets.map((a) => [a.link_id, a]));
        result = orderState.currentOrder
          .map((linkId) => assetMap.get(linkId))
          .filter((a): a is ProductAsset => a !== undefined);
      }

      const pendingItems = collectPendingItems(
        channelKey, result, pendingCopies, pendingUploads
      );
      insertPendingItems(result, pendingItems);
      updateSlotNumbers(result, hasReorder);

      return result;
    },
    [groupedByChannel, channelOrders, pendingCopies, pendingUploads, changedChannelKeys]
  );
}

type PendingItem = { virtualAsset: ProductAsset; insertAtIndex?: number };

function collectPendingItems(
  channelKey: string,
  existingAssets: ProductAsset[],
  pendingCopies: PendingCopy[],
  pendingUploads: PendingUpload[],
): PendingItem[] {
  const items: PendingItem[] = [];

  for (const pending of pendingCopies.filter((p) => p.targetChannelKey === channelKey)) {
    items.push({
      virtualAsset: {
        ...pending.asset,
        link_id: `pending-copy-${pending.sourceAssetId}-${pending.targetChannelId}`,
        channel_id: pending.targetChannelId,
        channel_name: channelKey,
        slot: 0,
        is_pending: true,
      },
      insertAtIndex: pending.insertAtIndex,
    });
  }

  for (const pending of pendingUploads.filter((p) => p.channelKey === channelKey)) {
    const pendingLinkId = `pending-upload-${pending.assetId}-${channelKey}`;
    if (existingAssets.some((a) => a.link_id === pendingLinkId) ||
        items.some((p) => p.virtualAsset.link_id === pendingLinkId)) {
      continue;
    }
    items.push({
      virtualAsset: {
        link_id: pendingLinkId,
        asset_id: pending.assetId,
        channel_id: pending.channelId,
        channel_name: channelKey,
        channel_type: null,
        slot: 0,
        sort_number: 0,
        alt_text: null,
        s3_key_original: null,
        s3_url: null,
        width: pending.width,
        height: pending.height,
        mime_type: null,
        content_hash: null,
        is_pending: true,
      },
      insertAtIndex: pending.insertAtIndex,
    });
  }

  items.sort((a, b) => {
    if (a.insertAtIndex === undefined && b.insertAtIndex === undefined) return 0;
    if (a.insertAtIndex === undefined) return 1;
    if (b.insertAtIndex === undefined) return -1;
    return a.insertAtIndex - b.insertAtIndex;
  });

  return items;
}

function insertPendingItems(result: ProductAsset[], items: PendingItem[]) {
  for (const { virtualAsset, insertAtIndex } of items) {
    if (insertAtIndex !== undefined && insertAtIndex <= result.length) {
      result.splice(insertAtIndex, 0, virtualAsset);
    } else {
      result.push(virtualAsset);
    }
  }
}

function updateSlotNumbers(result: ProductAsset[], hasReorder: boolean) {
  if (hasReorder) {
    // After reorder: sequential slots (user changed the order)
    result.forEach((asset, idx) => {
      asset.slot = idx + 1;
      if ('sort_number' in asset) {
        asset.sort_number = idx + 1;
      }
    });
  } else {
    // No reorder: preserve original slots from DB/JTL, only assign to pending
    const maxExistingSlot = Math.max(...result.filter(a => a.slot > 0).map(a => a.slot), 0);
    let nextSlot = maxExistingSlot + 1;
    for (const asset of result) {
      if (asset.slot === 0 || asset.is_pending) {
        asset.slot = nextSlot;
        if ('sort_number' in asset) {
          asset.sort_number = nextSlot;
        }
        nextSlot++;
      }
    }
  }
}
